const CART_DRAWER_V2_UPSELL_SIZE_SUFFIXES = Object.freeze({
  Single: 'single',
  'Small Double': 'small-double',
  Double: 'double',
  King: 'king',
  'Super King': 'super-king',
});

const CART_DRAWER_V2_UPSELL_MATTRESS_CONFIGS = Object.freeze([
  {
    displayName: 'Classic Hybrid Mattress',
    handle: 'classic-hybrid-mattress',
    searchTerms: ['classic hybrid'],
    prefix: 'the-nectar-classic-hybrid-starter-bundle',
    priority: 1,
  },
  {
    displayName: 'Premier Hybrid Mattress',
    handle: 'premier-hybrid-mattress',
    searchTerms: ['premier hybrid'],
    prefix: 'the-nectar-premier-hybrid-starter-bundle',
    priority: 1,
  },
  {
    displayName: 'Premier Memory Foam Mattress',
    handle: 'premier-memory-foam-mattress',
    searchTerms: ['premier memory foam'],
    prefix: 'the-nectar-premier-starter-bundle',
    priority: 2,
  },
  {
    displayName: 'Classic Memory Foam Mattress',
    handle: 'classic-memory-foam-mattress',
    searchTerms: ['classic memory foam'],
    prefix: 'starter-bundle',
    priority: 4,
    sizeSuffixes: {
      Single: 'single',
      'Small Double': 'small-double-size',
      Double: 'double-size',
      King: 'king-size',
      'Super King': 'super-king-size',
    },
  },
]);

if (!customElements.get('cart-drawer-v2-upsell')) {
  class CartDrawerV2Upsell extends HTMLElement {
    constructor() {
      super();

      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);

      this.productCache = new Map();
      this.bundleConfigCache = new Map();
      this.refreshPromise = Promise.resolve();
      this.dragState = null;
      this.pendingOfferHandle = null;
    }

    connectedCallback() {
      if (this.connectedOnce) {
        this.bindEvents();
        this.queueRefresh();
        return;
      }

      this.connectedOnce = true;
      this.config = this.parseConfig();

      if (!this.config.enabled) {
        this.hidden = true;
        return;
      }

      this.bindEvents();
      this.queueRefresh();
    }

    disconnectedCallback() {
      this.unbindEvents();
    }

    parseConfig() {
      const rawConfig = this.querySelector('[data-cart-drawer-v2-upsell-config]')?.textContent;

      if (!rawConfig) {
        return { enabled: false };
      }

      try {
        return JSON.parse(rawConfig);
      } catch (error) {
        console.error('[CartDrawerV2Upsell] Failed to parse config', error);
        return { enabled: false };
      }
    }

    bindEvents() {
      if (this.bound) {
        return;
      }

      this.bound = true;
      document.addEventListener('liquid-ajax-cart:queue-end', this.handleCartUpdate);
      document.addEventListener('cart:updated', this.handleCartUpdate);
      this.addEventListener('click', this.handleClick);
      window.addEventListener('pointermove', this.handlePointerMove);
      window.addEventListener('pointerup', this.handlePointerUp);
      window.addEventListener('pointercancel', this.handlePointerUp);
    }

    unbindEvents() {
      if (!this.bound) {
        return;
      }

      this.bound = false;
      document.removeEventListener('liquid-ajax-cart:queue-end', this.handleCartUpdate);
      document.removeEventListener('cart:updated', this.handleCartUpdate);
      this.removeEventListener('click', this.handleClick);
      window.removeEventListener('pointermove', this.handlePointerMove);
      window.removeEventListener('pointerup', this.handlePointerUp);
      window.removeEventListener('pointercancel', this.handlePointerUp);

      if (this.carousel) {
        this.carousel.removeEventListener('pointerdown', this.handlePointerDown);
      }
    }

    handleCartUpdate() {
      this.queueRefresh();
    }

    queueRefresh() {
      this.refreshPromise = this.refreshPromise
        .catch(() => undefined)
        .then(() => this.refresh());

      return this.refreshPromise;
    }

    async refresh() {
      const cart = await this.getCart();
      const state = this.resolveState(cart);

      if (!state) {
        this.hidden = true;
        this.innerHTML = this.querySelector('[data-cart-drawer-v2-upsell-config]')?.outerHTML || '';
        this.remountCartDrawerInteractions();
        return;
      }

      const offers = [];

      for (const offer of state.offerRequests) {
          const resolvedOffer = await this.resolveOffer(offer, state);

        if (resolvedOffer) {
          offers.push(resolvedOffer);
        }
      }

      if (!offers.length) {
        this.hidden = true;
        this.innerHTML = this.querySelector('[data-cart-drawer-v2-upsell-config]')?.outerHTML || '';
        this.remountCartDrawerInteractions();
        return;
      }

      this.currentState = state;
      this.currentOffers = offers;
      this.hidden = false;
      this.render();
      this.remountCartDrawerInteractions();
    }

    remountCartDrawerInteractions() {
      if (!window.__Theme?.remountComponent) {
        return;
      }

      const cartDrawer = this.closest('#CartDrawerV2');

      if (!cartDrawer) {
        return;
      }

      const remountTargets = [
        {
          selector: '.cart-drawer-v2__totals__total__toggle[data-class-change]',
          types: ['class-change'],
        },
        {
          selector: '[data-cart-drawer-v2-dividebuy-trigger]',
          types: ['class-change', 'lock-scroll'],
        },
        {
          selector: '[data-modal-cart-drawer-v2-dividebuy] .modal__close',
          types: ['also-click', 'class-change', 'lock-scroll', 'keyboard-event'],
          scope: 'document',
        },
      ];

      remountTargets.forEach(({ selector, types, scope }) => {
        const root = scope === 'document' ? document : cartDrawer;

        root.querySelectorAll(selector).forEach((element) => {
          types.forEach((type) => {
            window.__Theme.remountComponent(element, type);
          });
        });
      });
    }

    async getCart() {
      const cartState = window.liquidAjaxCart?.getState?.()?.cart;

      if (cartState) {
        return cartState;
      }

      if (window.liquidAjaxCart?.cart) {
        return window.liquidAjaxCart.cart;
      }

      const response = await fetch('/cart.js');

      if (!response.ok) {
        return null;
      }

      return response.json();
    }

    resolveState(cart) {
      const cartItems = cart?.items || [];

      if (!cartItems.length || this.hasBundle(cartItems)) {
        return null;
      }

      const mattressItem = this.getMattressItem(cartItems);

      if (!mattressItem) {
        return null;
      }

      const size = this.extractSize(mattressItem.variant_title || mattressItem.title);

      if (!size) {
        return null;
      }

      const isKids = this.isKidsMattress(mattressItem);
      const protectorItem = this.getMatchingProtector(cartItems, size, isKids);

      if (isKids) {
        if (protectorItem) {
          return null;
        }

        return {
          step: 'mattress-only',
          size,
          isKids,
          mattressItem,
          progressValue: this.config.progress?.mattressOnly || 25,
          offerRequests: [
            {
              kind: 'protector',
              handleCandidates: [this.config.kidsProtectorHandle],
            },
          ],
        };
      }

      if (protectorItem) {
        return {
          step: 'with-protector',
          size,
          isKids: false,
          mattressItem,
          protectorItem,
          progressValue: this.config.progress?.withProtector || 75,
          offerRequests: [
            {
              kind: 'bundle',
              bundleType: 'starter',
              handleCandidates: this.getStarterBundleCandidates(size, mattressItem),
            },
            {
              kind: 'bundle',
              bundleType: 'ottoman',
              handleCandidates: this.config.bundleHandleCandidates?.ottoman?.[size] || [],
            },
          ],
        };
      }

      return {
        step: 'mattress-only',
        size,
        isKids: false,
        mattressItem,
        progressValue: this.config.progress?.mattressOnly || 25,
        offerRequests: [
          {
            kind: 'protector',
            handleCandidates: [this.config.protectorHandle],
          },
          {
            kind: 'bundle',
            bundleType: 'starter',
              handleCandidates: this.getStarterBundleCandidates(size, mattressItem),
          },
        ],
      };
    }

    hasBundle(cartItems) {
      return cartItems.some((item) => {
        if (item.product?.tags?.includes('Shopify Bundle')) {
          return true;
        }

        if (item.handle?.includes('bundle')) {
          return true;
        }

        return Object.keys(item.properties || {}).some((key) => key.toLowerCase().includes('_bundleitem'));
      });
    }

    getMattressItem(cartItems) {
      return cartItems.find((item) => {
        return item.product_type === 'Mattress' || [
          'classic-memory-foam-mattress',
          'classic-hybrid-mattress',
          'premier-memory-foam-mattress',
          'premier-hybrid-mattress',
          'kids-mattress',
        ].some((handle) => item.handle?.includes(handle));
      });
    }

    getMatchingProtector(cartItems, size, isKids) {
      return cartItems.find((item) => {
        const isProtector = isKids
          ? item.handle === this.config.kidsProtectorHandle
          : item.handle === this.config.protectorHandle || item.title?.toLowerCase().includes('protector');

        if (!isProtector) {
          return false;
        }

        if (isKids) {
          return true;
        }

        const protectorSize = this.extractSize(item.variant_title || item.title);

        return !protectorSize || protectorSize === size;
      }) || null;
    }

    isKidsMattress(item) {
      return item.handle === 'kids-mattress' || item.title?.toLowerCase().includes('kids mattress');
    }

    extractSize(title) {
      if (!title) {
        return null;
      }

      return Object.keys(CART_DRAWER_V2_UPSELL_SIZE_SUFFIXES)
        .sort((a, b) => b.length - a.length)
        .find((size) => title.includes(size)) || null;
    }

    async resolveOffer(offerRequest, state) {
      const product = await this.fetchFirstProduct(offerRequest.handleCandidates || []);

      if (!product) {
        return null;
      }

      const variant = this.findVariant(product, offerRequest.kind === 'protector' ? state.size : null);

      if (!variant) {
        return null;
      }

      let bundleItems = null;
      let totalValue = null;

      if (offerRequest.kind === 'bundle') {
        bundleItems = await this.fetchBundleItems(product.handle, state.mattressItem);
        totalValue = this.config.bundleTotalValues?.[product.handle]?.[variant.id] || null;
      } else if (offerRequest.kind === 'protector') {
        totalValue = this.config.protectorTotalValues?.[variant.id] || null;
      }

      return {
        ...offerRequest,
        product,
        variant,
        bundleItems,
        totalValue,
      };
    }

    getStarterBundleCandidates(size, mattressItem) {
      const configuredCandidates = this.config.bundleHandleCandidates?.starter?.[size] || [];
      const mattressConfig = this.getMattressConfig(mattressItem);

      if (!mattressConfig) {
        return configuredCandidates;
      }

      const sizeSuffix = mattressConfig.sizeSuffixes?.[size] || CART_DRAWER_V2_UPSELL_SIZE_SUFFIXES[size];

      if (!sizeSuffix) {
        return configuredCandidates;
      }

      return Array.from(new Set([`${mattressConfig.prefix}-${sizeSuffix}`, ...configuredCandidates]));
    }

    getMattressConfig(mattressItem) {
      if (!mattressItem) {
        return null;
      }

      const title = String(mattressItem.title || '').toLowerCase();

      return [...CART_DRAWER_V2_UPSELL_MATTRESS_CONFIGS]
        .sort((a, b) => (a.priority || 0) - (b.priority || 0))
        .find((config) => {
          return config.handle === mattressItem.handle || config.searchTerms.some((term) => title.includes(term));
        }) || null;
    }

    getMattressType(mattressItem) {
      return this.getMattressConfig(mattressItem)?.displayName || 'Mattress';
    }

    async fetchFirstProduct(handleCandidates) {
      for (const handle of handleCandidates) {
        const product = await this.fetchProduct(handle);

        if (product) {
          return product;
        }
      }

      return null;
    }

    async fetchProduct(handle) {
      if (!handle) {
        return null;
      }

      if (this.productCache.has(handle)) {
        return this.productCache.get(handle);
      }

      try {
        const response = await fetch(`/products/${handle}.js`);

        if (!response.ok) {
          this.productCache.set(handle, null);
          return null;
        }

        const product = await response.json();
        this.productCache.set(handle, product);
        return product;
      } catch (error) {
        this.productCache.set(handle, null);
        return null;
      }
    }

    findVariant(product, preferredSize) {
      if (!product?.variants?.length) {
        return null;
      }

      if (preferredSize) {
        const matchingVariant = product.variants.find((variant) => {
          return variant.available && this.extractSize(variant.title) === preferredSize;
        });

        if (matchingVariant) {
          return matchingVariant;
        }
      }

      return product.variants.find((variant) => variant.available) || null;
    }

    async fetchBundleItems(handle, mattressItem) {
      if (!handle) {
        return null;
      }

      const cacheKey = `${handle}::${mattressItem?.handle || 'default'}`;

      if (this.bundleConfigCache.has(cacheKey)) {
        return this.bundleConfigCache.get(cacheKey);
      }

      const bundleItems = [];

      if (mattressItem) {
        bundleItems.push(this.getMattressType(mattressItem));
      }

      try {
        const response = await fetch(`/products/${handle}?view=json`);

        if (!response.ok) {
          const result = bundleItems.length ? bundleItems : null;
          this.bundleConfigCache.set(cacheKey, result);
          return result;
        }

        const bundleConfig = await response.json();
        if (Array.isArray(bundleConfig?.bundleItems)) {
          bundleConfig.bundleItems.forEach((item) => {
            if (item) {
              bundleItems.push(this.titleCase(item));
            }
          });
        }

        const result = bundleItems.length ? bundleItems : null;

        this.bundleConfigCache.set(cacheKey, result);
        return result;
      } catch (error) {
        const result = bundleItems.length ? bundleItems : null;
        this.bundleConfigCache.set(cacheKey, result);
        return result;
      }
    }

    titleCase(value) {
      return String(value)
        .split(' ')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');
    }

    render() {
      const description = this.buildDescription();
      const configScript = this.querySelector('[data-cart-drawer-v2-upsell-config]')?.outerHTML || '';

      this.innerHTML = `${configScript}
        <div class="cart-drawer-v2-upsell">
          <div class="cart-drawer-v2-upsell__surface">
            <div class="cart-drawer-v2-upsell__header">
              <h3 class="cart-drawer-v2-upsell__heading">${this.config.title}</h3>
              <div class="cart-drawer-v2-upsell__progress" aria-hidden="true">
                <span class="cart-drawer-v2-upsell__progress-bar" style="width: ${this.currentState.progressValue}%"></span>
              </div>
            </div>
            <p class="cart-drawer-v2-upsell__description">${description}</p>
            <div class="cart-drawer-v2-upsell__carousel" data-carousel>
              <div class="cart-drawer-v2-upsell__track" data-carousel-track>
                ${this.currentOffers.map((offer, index) => this.renderCard(offer, index)).join('')}
              </div>
            </div>
          </div>
        </div>
      `;

      this.carousel = this.querySelector('[data-carousel]');

      if (this.carousel) {
        this.carousel.removeEventListener('pointerdown', this.handlePointerDown);
        this.carousel.addEventListener('pointerdown', this.handlePointerDown);
      }
    }

    renderCard(offer, index) {
      const image = offer.product.featured_image;
      const cleanTitle = this.escapeHtml(this.cleanTitle(offer.product.title));
      const displayPrice = this.escapeHtml(`+${this.formatMoney(offer.variant.price)}`);
      const detailsSize = this.escapeHtml(offer.variant.title || this.currentState.size || 'Default Title');
      const priceData = this.escapeHtml(String(offer.variant.price));
      const comparePriceData = this.escapeHtml(String(offer.variant.compare_at_price || ''));
      const url = this.escapeHtml(`/products/${offer.product.handle}`);
      const isLoading = this.pendingOfferHandle === offer.product.handle;

      return `
        <article class="cart-drawer-v2-upsell__card${isLoading ? ' is-loading' : ''}" data-offer-index="${index}" data-offer-handle="${this.escapeHtml(offer.product.handle)}">
          <div class="cart-drawer-v2-upsell__media">
            <img class="cart-drawer-v2-upsell__image" src="${this.escapeHtml(image)}" alt="${cleanTitle}" loading="lazy" width="160" height="160">
          </div>
          <div class="cart-drawer-v2-upsell__content">
            <h4 class="cart-drawer-v2-upsell__title">${cleanTitle.toLowerCase()}</h4>
            <div class="cart-drawer-v2-upsell__meta">
              <modal-opener class="product-popup-modal__opener no-js-hidden" data-modal="#PopupModal-product-description">
                <button
                  class="cart-drawer-v2-upsell__details"
                  type="button"
                  data-size="${detailsSize}"
                  data-price="${priceData}"
                  data-compare_at_price="${comparePriceData}"
                  data-url="${url}"
                  aria-haspopup="dialog"
                >
                  <span>Details</span>
                  <span class="cart-drawer-v2-upsell__details-icon" aria-hidden="true">+</span>
                </button>
              </modal-opener>
              <p class="cart-drawer-v2-upsell__price">${displayPrice}</p>
              <div class="cart-drawer-v2-upsell__actions">
                <button class="cart-drawer-v2-upsell__add" type="button" data-upsell-add="${index}" ${isLoading ? 'disabled' : ''}>
                  <span
                    class="svg-wrapper cart-drawer-v2-upsell__add-icon"
                    aria-hidden="true"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 15.8333C9.76389 15.8333 9.56597 15.7535 9.40625 15.5938C9.24652 15.434 9.16666 15.2361 9.16666 15V10.8333H5C4.76389 10.8333 4.56597 10.7535 4.40625 10.5938C4.24653 10.434 4.16666 10.2361 4.16666 10C4.16666 9.76389 4.24653 9.56598 4.40625 9.40625C4.56597 9.24653 4.76389 9.16667 5 9.16667H9.16666V5.00001C9.16666 4.76389 9.24652 4.56598 9.40625 4.40626C9.56597 4.24653 9.76389 4.16667 10 4.16667C10.2361 4.16667 10.434 4.24653 10.5937 4.40626C10.7535 4.56598 10.8333 4.76389 10.8333 5.00001V9.16667H15C15.2361 9.16667 15.434 9.24653 15.5937 9.40625C15.7535 9.56598 15.8333 9.76389 15.8333 10C15.8333 10.2361 15.7535 10.434 15.5937 10.5938C15.434 10.7535 15.2361 10.8333 15 10.8333H10.8333V15C10.8333 15.2361 10.7535 15.434 10.5937 15.5938C10.434 15.7535 10.2361 15.8333 10 15.8333Z" fill="currentColor"/>
                    </svg>
                  </span>
                  <span>Add</span>
                </button>
              </div>
            </div>
          </div>
        </article>
      `;
    }

    buildDescription() {
      if (this.currentState.step === 'mattress-only') {
        const protectorOffer = this.currentOffers.find((offer) => offer.kind === 'protector');
        const lowestPrice = protectorOffer ? this.formatMoney(protectorOffer.variant.price) : this.formatMoney(this.currentOffers[0].variant.price);
        const savingPercent = this.getMaxSavingsPercent();

        const protectorUpgradeText = this.currentState.isKids
          ? this.config.kidsProtectorUpgradeText
          : this.config.protectorUpgradeText;

        if (savingPercent > 0) {
          return protectorUpgradeText
            .replace('[PROTECTOR_COST]', this.escapeHtml(lowestPrice))
            .replace('[BUNDLE_PERCENTAGE]', savingPercent);
        }

        return protectorUpgradeText
          .replace('[PROTECTOR_COST]', this.escapeHtml(lowestPrice))
          .replace('[BUNDLE_PERCENTAGE]', '0');
      }

      const savingPercent = this.getMaxSavingsPercent();

      if (savingPercent > 0) {
        return this.config.bundleUpgradeText
          .replace('[BUNDLE_PERCENTAGE]', savingPercent);
      }

      return this.config.bundleUpgradeText
        .replace('[BUNDLE_PERCENTAGE]', '0');
    }

    getOfferSavingsBaseline(offer) {
      if (!offer || offer.kind !== 'bundle') {
        return 0;
      }

      const compareAtPrice = Number(offer.variant?.compare_at_price || 0);

      if (compareAtPrice > 0) {
        return compareAtPrice;
      }

      const totalValue = Number(offer.totalValue || 0);

      if (totalValue > 0) {
        return totalValue * 100;
      }

      return 0;
    }

    getMaxSavingsPercent() {
      if (this.config.overrideBundleDiscountPercentage > 0) {
        return parseInt(this.config.overrideBundleDiscountPercentage, 10);
      }

      return this.currentOffers.reduce((maxValue, offer) => {
        const price = Number(offer.variant.price || 0);
        const savingsBaseline = this.getOfferSavingsBaseline(offer);

        if (savingsBaseline <= price || savingsBaseline <= 0) {
          return maxValue;
        }

        const savingsPercent = Math.round(((savingsBaseline - price) / savingsBaseline) * 100);

        if (!Number.isFinite(savingsPercent) || savingsPercent <= 0) {
          return maxValue;
        }

        return Math.max(maxValue, savingsPercent);
      }, 0);
    }

    cleanTitle(title) {
      return String(title)
        .replace(/^the nectar\s+/i, '')
        .replace(/^nectar\s+/i, '');
    }

    formatMoney(cents) {
      const moneyFormat = window.__Theme?._ctx?.getState?.()?.moneyFormat || window.Shopify?.money_format || '£{{amount}}';
      const value = (Number(cents || 0) / 100).toFixed(2);
      const [whole, fraction] = value.split('.');
      const formattedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const amount = fraction === '00' ? formattedWhole : `${formattedWhole}.${fraction}`;

      return moneyFormat.replace('{{amount}}', amount);
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = String(value ?? '');
      return div.innerHTML;
    }

    async handleClick(event) {
      const addButton = event.target.closest('[data-upsell-add]');

      if (!addButton) {
        return;
      }

      event.preventDefault();

      if (this.pendingOfferHandle) {
        return;
      }

      const offerIndex = Number(addButton.dataset.upsellAdd);
      const offer = this.currentOffers?.[offerIndex];

      if (!offer) {
        return;
      }

      this.pendingOfferHandle = offer.product.handle;
      this.render();

      try {
        const items = [
          {
            id: offer.variant.id,
            quantity: 1,
          },
        ];

        if (offer.kind === 'bundle' && Array.isArray(offer.bundleItems) && offer.bundleItems.length) {
          items[0].properties = offer.bundleItems.reduce((properties, item, index) => {
            properties[`_bundleItem${index + 1}`] = item;
            return properties;
          }, {});
        }

        if (window.liquidAjaxCart?.add) {
          await window.liquidAjaxCart.add(
            { items },
            {
              info: {
                cartDrawerV2Upsell: true,
                offerHandle: offer.product.handle,
                offerKind: offer.kind,
              },
            }
          );
        } else {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(items[0]),
          });

          await window.liquidAjaxCart?.update?.({}, { info: { cartDrawerV2Upsell: true } });
        }

        if (offer.kind === 'bundle') {
          await this.removeBundleUpgradeItems();
        }

        const cartDrawer = document.querySelector('cart-drawer-v2') || document.querySelector('cart-drawer');
        cartDrawer?.open?.();
      } catch (error) {
        console.error('[CartDrawerV2Upsell] Failed to add upsell product', error);
      } finally {
        this.pendingOfferHandle = null;
        this.queueRefresh();
      }
    }

    async removeBundleUpgradeItems() {
      const cart = await this.getCart();
      const updates = {};
      let mattressRemoved = false;
      let protectorRemoved = false;

      (cart?.items || []).forEach((item) => {
        if (this.isBundleLineItem(item)) {
          return;
        }

        if (!mattressRemoved && this.matchesMattress(item, this.currentState?.mattressItem)) {
          updates[item.key] = Math.max(Number(item.quantity || 0) - 1, 0);
          mattressRemoved = true;
          return;
        }

        if (!protectorRemoved && this.matchesProtector(item, this.currentState?.size, this.currentState?.isKids)) {
          updates[item.key] = Math.max(Number(item.quantity || 0) - 1, 0);
          protectorRemoved = true;
        }
      });

      if (!Object.keys(updates).length || !window.liquidAjaxCart?.update) {
        return;
      }

      await window.liquidAjaxCart.update(
        { updates },
        {
          info: {
            cartDrawerV2Upsell: true,
            bundleUpgrade: true,
          },
        }
      );
    }

    isBundleLineItem(item) {
      return Object.keys(item?.properties || {}).some((key) => key.toLowerCase().includes('_bundleitem'));
    }

    matchesMattress(item, targetMattress) {
      if (!targetMattress) {
        return false;
      }

      return item.key === targetMattress.key || (item.handle === targetMattress.handle && item.title === targetMattress.title);
    }

    matchesProtector(item, size, isKids) {
      if (isKids) {
        return item.handle === this.config.kidsProtectorHandle;
      }

      const isProtector = item.handle === this.config.protectorHandle || item.title?.toLowerCase().includes('protector');

      if (!isProtector) {
        return false;
      }

      const protectorSize = this.extractSize(item.variant_title || item.title);

      return !protectorSize || protectorSize === size;
    }

    handlePointerDown(event) {
      if (!this.carousel || event.target.closest('button, modal-opener, a, input, select, textarea')) {
        return;
      }

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: this.carousel.scrollLeft,
      };

      this.carousel.classList.add('is-dragging');
    }

    handlePointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId || !this.carousel) {
        return;
      }

      const deltaX = event.clientX - this.dragState.startX;
      this.carousel.scrollLeft = this.dragState.startScrollLeft - deltaX;
    }

    handlePointerUp(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId || !this.carousel) {
        return;
      }

      this.dragState = null;
      this.carousel.classList.remove('is-dragging');
    }
  }

  customElements.define('cart-drawer-v2-upsell', CartDrawerV2Upsell);
}
